import React, { useState, useRef, useEffect } from 'react';
import { Send, Paperclip, Smile, AtSign } from 'lucide-react';
import api from '../../services/api';

const MessageInput = ({ onSend, disabled, onTyping, channelId }) => {
  const [message, setMessage] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionPosition, setMentionPosition] = useState(0);
  const [channelMembers, setChannelMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const inputRef = useRef(null);
  const mentionDropdownRef = useRef(null);

  // Load channel members when channelId changes
  useEffect(() => {
    if (channelId) {
      loadChannelMembers();
    }
  }, [channelId]);

  // Filter members based on search
  useEffect(() => {
    if (mentionSearch) {
      const filtered = channelMembers.filter(member =>
        member.username.toLowerCase().includes(mentionSearch.toLowerCase()) ||
        (member.display_name && member.display_name.toLowerCase().includes(mentionSearch.toLowerCase()))
      );
      setFilteredMembers(filtered);
      setSelectedMentionIndex(0);
    } else {
      setFilteredMembers(channelMembers);
      setSelectedMentionIndex(0);
    }
  }, [mentionSearch, channelMembers]);

  const loadChannelMembers = async () => {
    try {
      const response = await api.get(`/sigil/channels/${channelId}`);
      const members = response.data.members || [];

      // Always add Astraea AI as a mentionable option
      const astraeaExists = members.some(m => m.username === 'astraea');
      if (!astraeaExists) {
        members.push({
          user_id: 'astraea',
          username: 'astraea',
          display_name: 'Astraea AI',
          is_ai: true
        });
      }

      setChannelMembers(members);
    } catch (error) {
      console.error('Error loading channel members:', error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      onSend(message);
      setMessage('');
      setShowMentions(false);
    }
  };

  const handleKeyDown = (e) => {
    if (showMentions && filteredMembers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex(prev =>
          prev < filteredMembers.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex(prev =>
          prev > 0 ? prev - 1 : filteredMembers.length - 1
        );
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredMembers[selectedMentionIndex]);
      } else if (e.key === 'Escape') {
        setShowMentions(false);
      }
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const insertMention = (member) => {
    const beforeMention = message.substring(0, mentionPosition);
    const afterMention = message.substring(inputRef.current?.selectionStart || message.length);
    const newMessage = `${beforeMention}@${member.username} ${afterMention}`;
    setMessage(newMessage);
    setShowMentions(false);
    setMentionSearch('');

    // Focus back on input
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPos = beforeMention.length + member.username.length + 2;
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setMessage(value);

    // Check for @ mention trigger
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Check if we're in a mention context (no space after @)
      if (!textAfterAt.includes(' ')) {
        setShowMentions(true);
        setMentionPosition(lastAtIndex);
        setMentionSearch(textAfterAt);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }

    // Notify parent about typing
    if (onTyping && value.length > 0) {
      onTyping();
    }
  };

  const handleMentionButtonClick = () => {
    const cursorPos = inputRef.current?.selectionStart || message.length;
    const newMessage = message.substring(0, cursorPos) + '@' + message.substring(cursorPos);
    setMessage(newMessage);
    setMentionPosition(cursorPos);
    setShowMentions(true);
    setMentionSearch('');

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(cursorPos + 1, cursorPos + 1);
      }
    }, 0);
  };

  return (
    <form onSubmit={handleSubmit} className="flex items-end gap-3 relative">
      <div className="flex-1 relative">
        <textarea
          ref={inputRef}
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Type a message... Use @ to mention someone"
          disabled={disabled}
          rows={1}
          className="w-full px-4 py-3 pr-32 bg-slate-800/50 border border-cyan-500/30 rounded-xl resize-none text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 disabled:bg-slate-800/30 disabled:text-slate-600 transition"
          style={{ minHeight: '48px', maxHeight: '120px' }}
        />

        {/* Mention Dropdown */}
        {showMentions && filteredMembers.length > 0 && (
          <div
            ref={mentionDropdownRef}
            className="absolute bottom-full left-0 mb-2 w-64 max-h-48 overflow-y-auto bg-slate-800 border border-cyan-500/30 rounded-xl shadow-xl z-50"
          >
            <div className="p-2 text-xs text-cyan-400 border-b border-cyan-500/20 flex items-center gap-1">
              <AtSign size={12} />
              Mention a team member
            </div>
            {filteredMembers.map((member, index) => (
              <button
                key={member.user_id || member.id}
                type="button"
                onClick={() => insertMention(member)}
                className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-cyan-500/20 transition text-left ${
                  index === selectedMentionIndex ? 'bg-cyan-500/20' : ''
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white text-sm font-semibold">
                  {member.username.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="text-white text-sm font-medium">
                    {member.display_name || member.username}
                  </div>
                  {member.display_name && (
                    <div className="text-slate-400 text-xs">@{member.username}</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* No members found */}
        {showMentions && filteredMembers.length === 0 && mentionSearch && (
          <div className="absolute bottom-full left-0 mb-2 w-64 bg-slate-800 border border-cyan-500/30 rounded-xl shadow-xl p-4 text-center">
            <p className="text-slate-400 text-sm">No members found</p>
          </div>
        )}

        <div className="absolute right-3 bottom-3 flex items-center gap-2">
          <button
            type="button"
            onClick={handleMentionButtonClick}
            className="p-1.5 text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition"
            title="Mention someone (@)"
          >
            <AtSign size={18} />
          </button>
          <button
            type="button"
            className="p-1.5 text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition"
            title="Add Emoji"
          >
            <Smile size={18} />
          </button>
          <button
            type="button"
            className="p-1.5 text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition"
            title="Attach File"
          >
            <Paperclip size={18} />
          </button>
        </div>
      </div>
      <button
        type="submit"
        disabled={!message.trim() || disabled}
        className="px-5 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl hover:from-cyan-600 hover:to-blue-600 disabled:from-slate-700 disabled:to-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed transition flex items-center gap-2 shadow-lg shadow-cyan-500/30 disabled:shadow-none"
      >
        <Send size={18} />
        <span className="font-medium">Send</span>
      </button>
    </form>
  );
};

export default MessageInput;
